import React, { useState, useEffect, useCallback, useRef } from 'react';
import './MerchantTestPage.css';

interface LogEntry {
  ts: string;
  type: 'event' | 'response' | 'error' | 'info';
  message: string;
  data?: any;
}

function generateUUID() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = Math.random() * 16 | 0;
    return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
  });
}

const PRODUCTS = [
  { id: 1, emoji: '👕', name: 'T-Shirt', description: 'Classic cotton t-shirt', price: 19.99 },
  { id: 2, emoji: '🧥', name: 'Hoodie', description: 'Premium pullover hoodie', price: 49.99 },
  { id: 3, emoji: '☕', name: 'Mug', description: 'Ceramic coffee mug 12oz', price: 9.99 },
  { id: 4, emoji: '🎨', name: 'Sticker Pack', description: 'Set of 10 vinyl stickers', price: 4.99 },
  { id: 5, emoji: '🧢', name: 'Cap', description: 'Adjustable snapback cap', price: 24.99 },
  { id: 6, emoji: '👜', name: 'Tote Bag', description: 'Canvas tote bag', price: 14.99 },
];

export default function MerchantTestPage() {
  const [sdkLoaded, setSdkLoaded] = useState(!!(window as any).PlzPay);
  const [tokens, setTokens] = useState<any[]>([]);
  const [activeTab, setActiveTab] = useState<'params' | 'catalog'>('params');
  const [form, setForm] = useState({
    token: '',
    amount: '10',
    currency: 'USDT',
    orderId: generateUUID().substring(0, 12),
    description: 'Test payment',
    callbackUrl: '',
    comebackUrl: `${window.location.origin}/merchant/test?success=1`,
    iframe: true,
    ttl: '60',
    sandbox: true,
  });
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [running, setRunning] = useState(false);
  const [lastError, setLastError] = useState<string | null>(null);
  const [loadingProducts, setLoadingProducts] = useState<Record<number, boolean>>({});
  const logsEndRef = useRef<HTMLDivElement>(null);

  // Load SDK dynamically
  useEffect(() => {
    if ((window as any).PlzPay) { setSdkLoaded(true); return; }
    const script = document.createElement('script');
    script.src = `${window.location.origin}/sdk/plzpay.js`;
    script.onload = () => setSdkLoaded(true);
    script.onerror = () => setSdkLoaded(false);
    document.head.appendChild(script);
    return () => { try { document.head.removeChild(script); } catch {} };
  }, []);

  useEffect(() => {
    fetch('/api/merchant/tokens', { credentials: 'include' })
      .then(r => r.json())
      .then(data => {
        const pub = data.filter((t: any) => t.type === 'publishable' && t.isActive);
        setTokens(pub);
        if (pub.length > 0 && pub[0].token) {
          setForm(f => ({ ...f, token: pub[0].token }));
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    logsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [logs]);

  // Listen for postMessages from checkout iframe
  useEffect(() => {
    const handler = (event: MessageEvent) => {
      if (!event.data || typeof event.data !== 'object') return;
      const { type, ...rest } = event.data;
      if (type?.startsWith('plzpay:')) {
        addLog('event', `postMessage: ${type}`, rest);
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, []);

  const addLog = useCallback((type: LogEntry['type'], message: string, data?: any) => {
    const ts = new Date().toLocaleTimeString();
    setLogs(prev => [...prev.slice(-99), { ts, type, message, data }]);
  }, []);

  const handleOpen = useCallback(async () => {
    if (running) return;
    setRunning(true);
    addLog('info', 'Opening checkout…');

    const opts: any = {
      token: form.token.replace('...', ''),
      amount: parseFloat(form.amount),
      currency: form.currency,
      ttl: parseInt(form.ttl, 10),
      iframe: form.iframe,
      sandbox: form.sandbox,
    };
    if (form.orderId) opts.orderId = form.orderId;
    if (form.description) opts.description = form.description;
    if (form.callbackUrl) opts.callbackUrl = form.callbackUrl;
    if (form.comebackUrl) opts.comebackUrl = form.comebackUrl;

    opts.onSuccess = (data: any) => { setLastError(null); addLog('event', 'onSuccess', data); };
    opts.onError = (err: any) => { addLog('error', 'onError: ' + (err?.message ?? err)); };
    opts.onClose = () => { addLog('info', 'onClose (modal closed)'); };

    try {
      const PlzPay = (window as any).PlzPay;
      if (!PlzPay) {
        const msg = 'PlzPay SDK not ready yet. Wait a moment and try again.';
        addLog('error', msg);
        setLastError(msg);
        setRunning(false);
        return;
      }

      setLastError(null);
      const pay = new PlzPay(opts);
      const result = await pay.open();
      addLog('response', 'Checkout opened', result);
    } catch (err: any) {
      const msg = err?.message ?? String(err);
      addLog('error', msg);
      setLastError(msg);
    }
    setRunning(false);
  }, [form, running, addLog]);

  const handleBuy = useCallback(async (product: typeof PRODUCTS[0]) => {
    if (loadingProducts[product.id]) return;
    const PlzPay = (window as any).PlzPay;
    if (!PlzPay) {
      addLog('error', 'PlzPay SDK not ready yet');
      return;
    }
    if (!form.token) {
      addLog('error', 'No token — set it on the Parameters tab first');
      return;
    }

    setLoadingProducts(prev => ({ ...prev, [product.id]: true }));
    addLog('info', `Opening checkout for ${product.name}…`);

    const opts = {
      token: form.token,
      amount: product.price,
      currency: 'USDT',
      orderId: `product-${product.id}-${Date.now()}`,
      description: product.name,
      onSuccess: (data: any) => addLog('event', `onSuccess: ${product.name}`, data),
      onError: (err: any) => addLog('error', `onError: ${err?.message ?? err}`),
      onClose: () => addLog('info', `onClose: ${product.name}`),
    };

    try {
      const pay = new PlzPay(opts);
      const result = await pay.open();
      addLog('response', `Checkout opened: ${product.name}`, result);
    } catch (err: any) {
      addLog('error', err?.message ?? String(err));
    }
    setLoadingProducts(prev => ({ ...prev, [product.id]: false }));
  }, [form.token, loadingProducts, addLog]);

  const set = useCallback((key: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const val = e.target.type === 'checkbox' ? (e.target as HTMLInputElement).checked : e.target.value;
    setForm(f => ({ ...f, [key]: val }));
  }, []);

  const clearLogs = useCallback(() => setLogs([]), []);

  return (
    <div className="test-page">
      <div className="test-header">
        <h1>SDK Sandbox</h1>
        <p>
          Interactive test environment for the PlzPay JS SDK.{' '}
          <span className={`test-sdk-status ${sdkLoaded ? 'loaded' : 'loading'}`}>
            {sdkLoaded ? '✓ SDK ready' : '⏳ Loading SDK…'}
          </span>
        </p>
      </div>

      <div className="test-layout">
        {/* Left panel with tabs */}
        <div className="test-form-panel">
          <div className="test-panel-tabs">
            <button
              className={`test-panel-tab ${activeTab === 'params' ? 'active' : ''}`}
              onClick={() => setActiveTab('params')}
            >
              Parameters
            </button>
            <button
              className={`test-panel-tab ${activeTab === 'catalog' ? 'active' : ''}`}
              onClick={() => setActiveTab('catalog')}
            >
              Catalog Demo
            </button>
          </div>

          {activeTab === 'params' && (
            <>
              <div className="test-field">
                <label>Publishable Token</label>
                {tokens.length > 0 && (
                  <select
                    value={form.token}
                    onChange={e => setForm(f => ({ ...f, token: e.target.value }))}
                    style={{ marginBottom: '8px' }}
                  >
                    {tokens.map(t => (
                      <option key={t.id} value={t.token ?? ''}>
                        {t.name} ({t.prefix}…)
                      </option>
                    ))}
                  </select>
                )}
                <input
                  type="text"
                  value={form.token}
                  onChange={set('token')}
                  placeholder="plzpay_pub_<paste full token here>"
                />
                {tokens.length === 0 && (
                  <div className="test-field-hint">
                    No active tokens yet. Create one on the{' '}
                    <a href="/merchant" target="_blank" rel="noreferrer">API Keys tab</a>.
                  </div>
                )}
              </div>

              <div className="test-row">
                <div className="test-field">
                  <label>Amount</label>
                  <input type="number" value={form.amount} onChange={set('amount')} min="0.01" step="0.01" />
                </div>
                <div className="test-field">
                  <label>Currency</label>
                  <select value={form.currency} onChange={set('currency')}>
                    <option>USDT</option>
                    <option>TRX</option>
                    <option>BTC</option>
                    <option>ETH</option>
                  </select>
                </div>
              </div>

              <div className="test-field">
                <label>Order ID (idempotency)</label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input type="text" value={form.orderId} onChange={set('orderId')} style={{ flex: 1 }} />
                  <button
                    className="test-btn-sm"
                    onClick={() => setForm(f => ({ ...f, orderId: generateUUID().substring(0, 12) }))}
                  >
                    Random
                  </button>
                </div>
              </div>

              <div className="test-field">
                <label>Description</label>
                <input type="text" value={form.description} onChange={set('description')} placeholder="Order #123" />
              </div>

              <div className="test-field">
                <label>Callback URL (server webhook)</label>
                <input type="text" value={form.callbackUrl} onChange={set('callbackUrl')} placeholder="https://your-server.com/webhook" />
              </div>

              <div className="test-field">
                <label>Comeback URL (redirect after payment)</label>
                <input type="text" value={form.comebackUrl} onChange={set('comebackUrl')} placeholder="https://your-site.com/success" />
              </div>

              <div className="test-row">
                <div className="test-field">
                  <label>TTL (minutes)</label>
                  <input type="number" value={form.ttl} onChange={set('ttl')} min="5" max="1440" />
                </div>
                <div className="test-field">
                  <label>Mode</label>
                  <div className="test-toggle">
                    <label className="test-switch">
                      <input type="checkbox" checked={form.iframe} onChange={set('iframe')} />
                      <span className="test-slider" />
                    </label>
                    <span>{form.iframe ? 'Iframe modal' : 'Redirect'}</span>
                  </div>
                </div>
              </div>

              <div className="test-field">
                <label>Sandbox mode</label>
                <div className="test-toggle">
                  <label className="test-switch">
                    <input type="checkbox" checked={form.sandbox} onChange={set('sandbox')} />
                    <span className="test-slider" />
                  </label>
                  <span style={{ color: form.sandbox ? '#f59e0b' : 'inherit' }}>
                    {form.sandbox ? '🧪 Sandbox (no real funds)' : 'Production'}
                  </span>
                </div>
              </div>

              <button
                className="test-open-btn"
                onClick={handleOpen}
                disabled={running || !form.token}
              >
                {running ? '⏳ Opening…' : '▶ Open Checkout'}
              </button>

              {lastError && (
                <div className="test-error-banner">
                  <strong>Error:</strong> {lastError}
                </div>
              )}
            </>
          )}

          {activeTab === 'catalog' && (
            <>
              {!form.token && (
                <div className="catalog-token-hint">
                  ⚠️ Set your publishable token on the <strong>Parameters</strong> tab first.
                </div>
              )}
              <p className="catalog-intro">
                Each "Buy Now" click creates a unique checkout session with{' '}
                <code>orderId = product-{'{id}'}-{'{timestamp}'}</code>.
                Double-clicks are blocked while the modal is loading.
              </p>
              <div className="catalog-grid">
                {PRODUCTS.map(product => (
                  <div key={product.id} className="catalog-item">
                    <div className="catalog-item-emoji">{product.emoji}</div>
                    <div className="catalog-item-name">{product.name}</div>
                    <div className="catalog-item-desc">{product.description}</div>
                    <div className="catalog-item-price">${product.price.toFixed(2)}</div>
                    <button
                      className="catalog-buy-btn"
                      onClick={() => handleBuy(product)}
                      disabled={!!loadingProducts[product.id] || !form.token || !sdkLoaded}
                    >
                      {loadingProducts[product.id] ? 'Loading…' : 'Buy Now'}
                    </button>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Log panel */}
        <div className="test-log-panel">
          <div className="test-log-header">
            <h2>Event Log</h2>
            <button className="test-btn-sm" onClick={clearLogs}>Clear</button>
          </div>
          <div className="test-log-entries">
            {logs.length === 0 && (
              <div className="test-log-empty">Events will appear here…</div>
            )}
            {logs.map((log, i) => (
              <div key={i} className={`test-log-entry test-log-${log.type}`}>
                <span className="test-log-ts">{log.ts}</span>
                <span className="test-log-msg">{log.message}</span>
                {log.data && (
                  <pre className="test-log-data">{JSON.stringify(log.data, null, 2)}</pre>
                )}
              </div>
            ))}
            <div ref={logsEndRef} />
          </div>
        </div>
      </div>
    </div>
  );
}
